import { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Form, Modal, Spinner } from "react-bootstrap";
import toast from "react-hot-toast";

import {
  createSurveyPublicLink,
  disableSurveyPublicLink,
  listSurveyPublicLinks,
  messageFromError,
  rotateSurveyPublicLink,
  updateSurveyPublicLink,
} from "../../utils/api";

const dateInputValue = value => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const linkForm = (survey, link = null) => ({
  authenticationMode: link?.authenticationMode
    || (survey.authenticationMode === "anonymous" ? "anonymous" : "required"),
  allowMultipleResponses: link?.allowMultipleResponses
    ?? Boolean(survey.allowMultipleResponses),
  allowSubmittedResponseUpdates: link?.allowSubmittedResponseUpdates
    ?? Boolean(survey.allowSubmittedResponseUpdates),
  embeddable: link?.embeddable ?? true,
  opensAt: dateInputValue(link?.opensAt ?? survey.opensAt),
  closesAt: dateInputValue(link?.closesAt ?? survey.closesAt),
});

const linkPayload = form => ({
  ...form,
  opensAt: form.opensAt ? new Date(form.opensAt).toISOString() : null,
  closesAt: form.closesAt ? new Date(form.closesAt).toISOString() : null,
});

function PublicLinksCard({ survey }) {
  const [links, setLinks] = useState(null);
  const [error, setError] = useState("");
  const [reveal, setReveal] = useState(null);
  const [editor, setEditor] = useState(null);
  const [form, setForm] = useState(() => linkForm(survey));
  const [busy, setBusy] = useState(false);
  const [rotateTarget, setRotateTarget] = useState(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const result = await listSurveyPublicLinks(survey.uuid);
      setLinks(result.items || []);
    } catch (requestError) {
      setError(messageFromError(requestError, "Public links could not be loaded."));
    }
  }, [survey.uuid]);

  useEffect(() => { load(); }, [load]);

  const openEditor = link => {
    setEditor(link || { mode: "new" });
    setForm(linkForm(survey, link));
    setError("");
  };

  const change = event => {
    const { name, checked, type, value } = event.target;
    setForm(current => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const save = async event => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = editor?.mode === "new"
        ? await createSurveyPublicLink(survey.uuid, linkPayload(form))
        : await updateSurveyPublicLink(survey.uuid, editor.id, linkPayload(form));
      if (result.item?.url) setReveal(result.item);
      setEditor(null);
      toast.success(editor?.mode === "new" ? "Public link created." : "Public link settings saved.");
      await load();
    } catch (requestError) {
      setError(messageFromError(requestError, "The public link could not be saved."));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(reveal.url);
      toast.success("Public link copied.");
    } catch {
      setError("Copy was blocked by the browser. Select the link and copy it manually.");
    }
  };

  const rotate = async () => {
    const target = rotateTarget;
    setRotateTarget(null);
    setBusy(true);
    try {
      const result = await rotateSurveyPublicLink(survey.uuid, target.id);
      setReveal(result.item);
      toast.success("Public link rotated. The previous URL no longer works.");
      await load();
    } catch (requestError) {
      setError(messageFromError(requestError, "The public link could not be rotated."));
    } finally {
      setBusy(false);
    }
  };

  const setActive = async (link, active) => {
    setBusy(true);
    try {
      if (active) {
        await updateSurveyPublicLink(survey.uuid, link.id, { active: true });
      } else {
        await disableSurveyPublicLink(survey.uuid, link.id);
      }
      toast.success(active ? "Public link enabled." : "Public link disabled.");
      await load();
    } catch (requestError) {
      setError(messageFromError(requestError, "The public link could not be changed."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="fera-panel mt-4" aria-labelledby="public-links-title">
      <div className="fera-panel-heading">
        <div>
          <p className="fera-eyebrow">Distribution</p>
          <h2 id="public-links-title">Generic public links</h2>
          <p className="text-muted mb-0">Creating or copying a link never consumes request usage.</p>
        </div>
        <Button
          type="button"
          disabled={busy || survey.status === "deleted"}
          onClick={() => openEditor(null)}
        >
          Create link
        </Button>
      </div>
      {error ? <Alert variant="danger">{error}</Alert> : null}
      {reveal ? (
        <Alert variant="info" className="fera-link-reveal">
          <strong>Copy this link now.</strong>
          <span>This is a deliberate public code. Share it only where you want people to open the survey.</span>
          <div className="fera-copy-row">
            <Form.Control
              aria-label="New public survey link"
              value={reveal.url}
              readOnly
              onFocus={event => event.target.select()}
            />
            <Button type="button" onClick={copy}>Copy</Button>
            <Button
              type="button"
              variant="outline-primary"
              as="a"
              href={reveal.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open
            </Button>
          </div>
        </Alert>
      ) : null}
      {links === null ? (
        <div className="d-flex align-items-center gap-2 text-muted" role="status">
          <Spinner animation="border" size="sm" /> Loading public links&hellip;
        </div>
      ) : links.length ? (
        <div className="fera-public-link-list">
          {links.map(link => (
            <article key={link.id} className="fera-public-link-row">
              <div>
                <div className="d-flex align-items-center gap-2">
                  <strong>Link {link.keyPrefix}&hellip;</strong>
                  <Badge bg={link.status === "active" ? "success" : "secondary"}>
                    {link.status}
                  </Badge>
                </div>
                <small>
                  {link.authenticationMode} authentication
                  {" | "}
                  {link.allowMultipleResponses ? "multiple responses" : "one response"}
                  {" | "}
                  {link.allowSubmittedResponseUpdates ? "updates allowed" : "submitted responses locked"}
                  {" | "}
                  {link.embeddable ? "WordPress embedding enabled" : "embedding disabled"}
                </small>
              </div>
              <div className="fera-row-actions">
                {link.status !== "rotated" ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-secondary"
                    disabled={busy}
                    onClick={() => openEditor(link)}
                  >
                    Edit
                  </Button>
                ) : null}
                {link.status === "active" ? (
                  <>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline-secondary"
                      disabled={busy}
                      onClick={() => setActive(link, false)}
                    >
                      Disable
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline-danger"
                      disabled={busy}
                      onClick={() => setRotateTarget(link)}
                    >
                      Rotate
                    </Button>
                  </>
                ) : link.status === "disabled" ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-primary"
                    disabled={busy}
                    onClick={() => setActive(link, true)}
                  >
                    Enable
                  </Button>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      ) : (
        <p className="text-muted mb-0">No generic link has been created. Publish the survey before sharing a new link.</p>
      )}

      <Modal show={Boolean(editor)} onHide={() => !busy && setEditor(null)} centered>
        <Form onSubmit={save}>
          <Modal.Header closeButton>
            <Modal.Title>{editor?.mode === "new" ? "Create public link" : "Edit public link"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group controlId="public-link-authentication">
              <Form.Label>Authentication</Form.Label>
              <Form.Select name="authenticationMode" value={form.authenticationMode} onChange={change}>
                <option value="anonymous">Anonymous</option>
                <option value="optional">Optional Feradel sign-in</option>
                <option value="required">Feradel sign-in required</option>
              </Form.Select>
            </Form.Group>
            <Form.Check
              className="mt-3"
              type="switch"
              id="public-link-multiple"
              name="allowMultipleResponses"
              checked={form.allowMultipleResponses}
              onChange={change}
              label="Allow multiple responses per person"
            />
            <Form.Check
              className="mt-3"
              type="switch"
              id="public-link-updates"
              name="allowSubmittedResponseUpdates"
              checked={form.allowSubmittedResponseUpdates}
              onChange={change}
              label="Allow people to change their submitted responses"
            />
            <Form.Check
              className="mt-3"
              type="switch"
              id="public-link-embeddable"
              name="embeddable"
              checked={form.embeddable}
              onChange={change}
              label="Allow connected WordPress installations for this workspace to embed this link"
            />
            <div className="fera-link-schedule mt-3">
              <Form.Group controlId="public-link-opens">
                <Form.Label>Opens at</Form.Label>
                <Form.Control type="datetime-local" name="opensAt" value={form.opensAt} onChange={change} />
              </Form.Group>
              <Form.Group controlId="public-link-closes">
                <Form.Label>Closes at</Form.Label>
                <Form.Control type="datetime-local" name="closesAt" value={form.closesAt} onChange={change} />
              </Form.Group>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" variant="outline-secondary" disabled={busy} onClick={() => setEditor(null)}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? "Saving\u2026" : "Save link"}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={Boolean(rotateTarget)} onHide={() => setRotateTarget(null)} centered>
        <Modal.Header closeButton><Modal.Title>Rotate public link?</Modal.Title></Modal.Header>
        <Modal.Body>
          The current URL will stop working immediately. Existing responses remain available to their verified identities through the replacement link.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setRotateTarget(null)}>Cancel</Button>
          <Button variant="danger" onClick={rotate}>Rotate link</Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
}

export default PublicLinksCard;
